<template>
  <Transition name="login-prompt-fade">
    <div v-if="modelValue" class="login-prompt-mask" @click.stop="handleMaskClick">
      <section class="login-prompt-modal" role="dialog" aria-modal="true" @click.stop>
        <button
          v-if="showClose"
          type="button"
          class="login-prompt-close"
          :aria-label="closeAriaLabel"
          @click="close"
        >
          <font-awesome-icon icon="xmark" />
        </button>

        <h3 class="login-prompt-title">{{ title }}</h3>
        <p class="login-prompt-desc">{{ description }}</p>
        <div class="login-prompt-actions">
          <button type="button" class="login-prompt-secondary" @click="cancel">
            {{ secondaryText }}
          </button>
          <button type="button" class="login-prompt-primary" @click="confirm">
            {{ primaryText }}
          </button>
        </div>
      </section>
    </div>
  </Transition>
</template>

<script setup>
/* global defineProps, defineEmits */
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '温馨提示',
  },
  description: {
    type: String,
    default: '登录以后可以解锁更多内容',
  },
  secondaryText: {
    type: String,
    default: '稍后再说',
  },
  primaryText: {
    type: String,
    default: '立即登录',
  },
  closeAriaLabel: {
    type: String,
    default: '关闭',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  closeOnMask: {
    type: Boolean,
    default: true,
  },
  closeOnConfirm: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue', 'cancel', 'confirm', 'close'])

const close = () => {
  emit('update:modelValue', false)
  emit('close')
}

const cancel = () => {
  close()
  emit('cancel')
}

const confirm = () => {
  if (props.closeOnConfirm) {
    close()
  }

  emit('confirm')
}

const handleMaskClick = () => {
  if (props.closeOnMask) {
    close()
  }
}
</script>

<style scoped>
.login-prompt-mask {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(53, 42, 30, 0.28);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.login-prompt-modal {
  position: relative;
  width: min(360px, 100%);
  padding: 30px 24px 24px;
  border-radius: 24px;
  background: linear-gradient(180deg, #fffdfb 0%, #fff8f2 100%);
  border: 1px solid rgba(255, 184, 136, 0.28);
  box-shadow:
    0 24px 50px rgba(118, 86, 55, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.88);
  text-align: center;
}

.login-prompt-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #b7aa9d;
  font-size: 20px;
  cursor: pointer;
}

.login-prompt-title {
  margin: 0;
  color: #2d3436;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.2;
}

.login-prompt-desc {
  margin: 12px 0 0;
  color: #7b6f66;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.6;
}

.login-prompt-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 24px;
}

.login-prompt-secondary,
.login-prompt-primary {
  min-height: 44px;
  border: none;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.login-prompt-secondary {
  background: rgba(241, 232, 222, 0.88);
  color: #7b6b5e;
}

.login-prompt-primary {
  background: linear-gradient(135deg, #ff8b2f 0%, #ff6d16 100%);
  color: #ffffff;
  box-shadow: 0 10px 18px rgba(255, 122, 24, 0.22);
}

.login-prompt-secondary:hover,
.login-prompt-primary:hover {
  transform: translateY(-1px);
}

.login-prompt-primary:hover {
  box-shadow: 0 12px 22px rgba(255, 122, 24, 0.28);
}

.login-prompt-fade-enter-active,
.login-prompt-fade-leave-active {
  transition: opacity 0.22s ease;
}

.login-prompt-fade-enter-from,
.login-prompt-fade-leave-to {
  opacity: 0;
}
</style>
